import type { VideoPlacement } from "@types";
import { AnnotatedScreenshot, type ScreenshotBox } from "@webapp/components/annotated-screenshot";
import { YouTubeVideo } from "@webapp/components/youtube-video";
import { isVideoKey } from "@webapp/utils/videos";
import type { Element, Text } from "hast";
import type { ComponentProps } from "react";
import Markdown, { type ExtraProps } from "react-markdown";
import { Link } from "react-router";
import remarkGfm from "remark-gfm";

const BOX_LINE = /^box\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+"(.+)"$/;

function fencedBlock(node: Element | undefined) {
  const code = node?.children[0];
  if (code?.type !== "element" || code.tagName !== "code") return null;
  const classNames = code.properties.className;
  const language = Array.isArray(classNames)
    ? String(classNames.find((c) => String(c).startsWith("language-")) ?? "").slice(9)
    : "";
  const body = code.children
    .filter((child): child is Text => child.type === "text")
    .map((child) => child.value)
    .join("")
    .trim();
  return { language, body };
}

// ```screenshot
// src: /docs/inbox/overview.png
// alt: The inbox with three sections
// box 4 6.4 17.9 56.8 "Support threads"   <- left% top% width% height% "label"
// ```
function parseScreenshot(body: string) {
  const lines = body.split("\n").map((line) => line.trim());
  const field = (name: string) =>
    lines
      .find((line) => line.startsWith(`${name}:`))
      ?.slice(name.length + 1)
      .trim() ?? "";
  const boxes: ScreenshotBox[] = lines.flatMap((line) => {
    const match = BOX_LINE.exec(line);
    if (!match) return [];
    const [, x, y, w, h, label] = match;
    return [{ x: Number(x), y: Number(y), w: Number(w), h: Number(h), label }];
  });
  return { src: field("src"), alt: field("alt"), boxes };
}

function MarkdownLink({ href = "", children, ...rest }: ComponentProps<"a"> & ExtraProps) {
  const { node: _node, ...anchorProps } = rest;
  if (href.startsWith("/")) {
    return (
      <Link to={href} {...anchorProps}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" {...anchorProps}>
      {children}
    </a>
  );
}

export function MarkdownArticle({
  markdown,
  videoPlacement,
}: {
  markdown: string;
  videoPlacement: VideoPlacement;
}) {
  return (
    <div className="prose prose-invert max-w-none prose-headings:tracking-tight prose-a:text-[#6CB6F7] prose-img:rounded-xl">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: MarkdownLink,
          pre: ({ node, children }) => {
            const block = fencedBlock(node);
            if (block?.language === "youtube" && isVideoKey(block.body)) {
              return (
                <div className="not-prose my-8">
                  <YouTubeVideo videoKey={block.body} placement={videoPlacement} />
                </div>
              );
            }
            if (block?.language === "screenshot") {
              return <AnnotatedScreenshot {...parseScreenshot(block.body)} />;
            }
            return <pre>{children}</pre>;
          },
        }}
      >
        {markdown}
      </Markdown>
    </div>
  );
}
